import _ from 'lodash'
import React from 'react'
import {useIntl} from 'react-intl'
import {useParams} from 'react-router-dom'

import {
    AspectRatio,
    Badge,
    Box,
    Divider,
    Flex,
    Grid,
    Heading,
    Image,
    Stack,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'

import {useBlogArticle} from '../../../clients/contentful/hooks/use-blogs-articles'
import Link from '../../../components/link'
import ProductScroller from '../../../components/product-scroller'
import {colors} from '../../../constants'
import BlogDetailRichText from './partials/blog-detail-rich-text'

const BlogArticle = () => {
    const {slug} = useParams()
    const {formatMessage} = useIntl()
    const {filteredData} = useBlogArticle(slug)
    const sectionItems = filteredData?.sectionsCollection?.items

    return (
        <Flex
            flexDirection="column"
            gap={{base: '17px', lg: '43px'}}
            padding={{base: '34px 15px', lg: '52px 200px'}}
            alignItems="center"
            justifyContent="center"
        >
            {_.map(sectionItems, (section, index) => {
                switch (section.typeName) {
                    case 'ContentGrid':
                        return (
                            <Flex
                                flexDirection="column"
                                maxWidth="1103px"
                                key={index}
                                gap="15px"
                                justifyContent="center"
                            >
                                <Heading fontSize="40px" fontWeight="300">
                                    {section?.heading}
                                </Heading>
                                <Text
                                    dangerouslySetInnerHTML={{
                                        __html: section.longDescription
                                    }}
                                />
                                <Grid
                                    gap={{base: '20px', lg: '42px'}}
                                    gridTemplateColumns={{
                                        md: `repeat(${section?.itemsCollection?.items?.length}, 1fr)`,
                                        lg: `repeat(${section?.itemsCollection?.items?.length}, 1fr)`
                                    }}
                                >
                                    {section?.itemsCollection?.items?.map(
                                        (item, index) => {
                                            if (item.typeName === 'Card') {
                                                return (
                                                    <Flex
                                                        key={index}
                                                        flexDirection="column"
                                                        gap={{
                                                            base: '12px',
                                                            lg: '16px'
                                                        }}
                                                    >
                                                        <AspectRatio
                                                            ratio={345 / 457}
                                                            borderRadius="12px"
                                                            overflow="hidden"
                                                        >
                                                            <Image
                                                                src={
                                                                    item
                                                                        ?.cardImage
                                                                        ?.desktop
                                                                        ?.url
                                                                }
                                                                alt={
                                                                    item.cardTitle
                                                                }
                                                            />
                                                        </AspectRatio>
                                                        <Stack gap="10px">
                                                            <Text
                                                                fontSize="24px"
                                                                fontWeight="300"
                                                            >
                                                                {
                                                                    item?.cardTitle
                                                                }
                                                            </Text>
                                                            {item?.cardCta && (
                                                                <Link
                                                                    {...item?.cardCta}
                                                                    buttonStyles={{
                                                                        backgroundColor:
                                                                            'transparent',
                                                                        background:
                                                                            'transparent',
                                                                        border: '1px solid',
                                                                        borderColor:
                                                                            colors.black,
                                                                        color: colors.black,
                                                                        maxWidth:
                                                                            '109px',
                                                                        padding:
                                                                            '6px 18px'
                                                                    }}
                                                                />
                                                            )}
                                                            <Text
                                                                fontSize="16px"
                                                                fontWeight="300"
                                                            >
                                                                {
                                                                    item?.description
                                                                }
                                                            </Text>
                                                        </Stack>
                                                    </Flex>
                                                )
                                            }
                                            return (
                                                <Flex
                                                    flexDirection="column"
                                                    justifyContent="flex-end"
                                                    key={index}
                                                    width="345px"
                                                    height="459px"
                                                    backgroundImage={
                                                        item?.cardImage?.desktop
                                                            ?.url
                                                    }
                                                    padding="18px 30px"
                                                    borderRadius="12px"
                                                >
                                                    <Stack gap="21px">
                                                        <Text
                                                            fontSize="24px"
                                                            fontWeight="300"
                                                        >
                                                            {item?.cardTitle}
                                                        </Text>
                                                        {item?.cardCta && (
                                                            <Link
                                                                {...item?.cardCta}
                                                                buttonStyles={{
                                                                    backgroundColor:
                                                                        'transparent',
                                                                    background:
                                                                        'transparent',
                                                                    border: '1px solid',
                                                                    borderColor:
                                                                        colors.black,
                                                                    color: colors.black,
                                                                    maxWidth:
                                                                        '109px',
                                                                    padding:
                                                                        '6px 18px'
                                                                }}
                                                            />
                                                        )}
                                                        <Text
                                                            fontSize="16px"
                                                            fontWeight="300"
                                                        >
                                                            {item?.description}
                                                        </Text>
                                                    </Stack>
                                                </Flex>
                                            )
                                        }
                                    )}
                                </Grid>
                            </Flex>
                        )
                    case 'List':
                        if (section.listType === 'badge') {
                            return (
                                <Stack direction="row" key={index}>
                                    {section?.nonReferenceItems &&
                                        section?.nonReferenceItems?.map(
                                            (item, index) => (
                                                <Badge
                                                    key={index}
                                                    padding="6px 22px"
                                                    fontSize="14px"
                                                    textTransform="uppercase"
                                                    backgroundColor={
                                                        colors.badgeLight
                                                    }
                                                    borderRadius="41px"
                                                    color={colors.black}
                                                >
                                                    {item}
                                                </Badge>
                                            )
                                        )}
                                </Stack>
                            )
                        }

                        return (
                            <Box
                                display={{base: 'none', lg: 'block'}}
                                backgroundColor={colors.pmCream}
                                borderRadius="8px"
                                width="100%"
                                key={index}
                                maxWidth="726px"
                                padding={{lg: '35px 38px'}}
                            >
                                <Heading
                                    fontWeight="400"
                                    fontSize="24px"
                                    marginBottom="23px"
                                >
                                    {section?.heading}
                                </Heading>
                                <Grid
                                    gap="12px"
                                    gridAutoFlow="column"
                                    gridTemplateRows={`repeat(${
                                        section?.nonReferenceItems?.length > 3
                                            ? Math.ceil(
                                                  section?.nonReferenceItems
                                                      ?.length / 2
                                              )
                                            : section?.nonReferenceItems?.length
                                    }, 1fr)`}
                                >
                                    {section?.nonReferenceItems?.map(
                                        (item, index) => (
                                            <Text key={index} fontSize="16px">
                                                {index + 1}.&nbsp;
                                                {item}
                                            </Text>
                                        )
                                    )}
                                </Grid>
                            </Box>
                        )

                    case 'Carousel':
                        return (
                            <ProductScroller
                                productCarouselContent={{
                                    carouselCollection: {items: [section]}
                                }}
                                py={{lg: '0px'}}
                                px={{base: '10px', lg: '0px'}}
                                showScrollerNav
                                maxWidth={{
                                    base: 'calc(100vw - 40px)',
                                    lg: 'calc(100vw - 32px)'
                                }}
                                itemWidth={{
                                    base: 'calc(50% - 6.5px)',
                                    md: 'calc(33.33% - 8.66px)'
                                }}
                                key={index}
                                cardsGap="13px"
                                scrollContainerId={section.sectionName}
                            />
                        )

                    case 'RichTextContent':
                        return (
                            <BlogDetailRichText
                                pageContent={section}
                                key={index}
                            />
                        )
                    case 'ArticleHeroSection':
                        return (
                            <Flex
                                key={index}
                                flexDirection="column"
                                gap="30px"
                                justifyContent="center"
                                textAlign="center"
                                width="726px"
                            >
                                <Heading
                                    fontSize={{base: '30px', lg: '48px'}}
                                    width="100%"
                                    fontWeight="400"
                                    textAlign="center"
                                >
                                    {section?.heading}
                                </Heading>
                                <Flex
                                    fontFamily="Roboto"
                                    fontSize="14px"
                                    lineHeight="19px"
                                    alignItems="center"
                                    flexDirection="column"
                                    gap="2px"
                                    color="#2B2F31"
                                >
                                    <Text>
                                        {formatMessage({
                                            id: 'blog.written_by',
                                            defaultMessage: 'Written by'
                                        })}{' '}
                                        <span
                                            style={{
                                                textDecoration: 'underline',
                                                color: 'black'
                                            }}
                                        >
                                            {filteredData.writtenBy}
                                        </span>{' '}
                                        |{' '}
                                        <span style={{color: '#7B7B7B'}}>
                                            {formatMessage({
                                                id: 'blog.updated_on',
                                                defaultMessage: 'Updated on'
                                            })}{' '}
                                            {new Intl.DateTimeFormat(
                                                'en-CA'
                                            ).format(
                                                new Date(
                                                    filteredData.publishedOn
                                                )
                                            )}
                                        </span>
                                    </Text>
                                    <Text>
                                        <span style={{color: '#7B7B7B'}}>
                                            {formatMessage({
                                                id: 'blog.certified_by',
                                                defaultMessage: 'Certified by'
                                            })}{' '}
                                            {filteredData.certifiedBy}
                                        </span>
                                    </Text>
                                </Flex>
                                <Box borderRadius="12px" overflow="hidden">
                                    <Image
                                        width="100%"
                                        height="100%"
                                        src={section?.image?.desktop?.url}
                                        alt="hero image"
                                        objectFit="cover"
                                        maxHeight={{base: '30vh', lg: '45vh'}}
                                    />
                                </Box>
                            </Flex>
                        )
                    case 'Content':
                        if (section.sectionName.includes('articleDetails___')) {
                            return (
                                <Box>
                                    <Text
                                        dangerouslySetInnerHTML={{
                                            __html: section.text
                                        }}
                                    ></Text>
                                </Box>
                            )
                        }
                        return (
                            <Box key={index}>
                                {section?.heading && (
                                    <Heading fontWeight="300" fontSize="48px">
                                        {section?.heading}
                                    </Heading>
                                )}
                                <Text
                                    width="100%"
                                    maxWidth="726px"
                                    align="center"
                                    paddingX="38px"
                                    marginBottom="52px"
                                    textAlign="justify"
                                >
                                    {section?.text}
                                </Text>
                            </Box>
                        )
                    case 'TextAsset':
                        return (
                            <Box
                                borderRadius="12px"
                                overflow="hidden"
                                marginBottom="28px"
                                key={index}
                            >
                                <Image
                                    width="100%"
                                    height="100%"
                                    src={section?.media?.desktop?.url}
                                    alt="hero image"
                                    objectFit="cover"
                                />
                            </Box>
                        )
                    default:
                        console.log('Unknown type')
                        return null
                }
            })}

            <Stack maxWidth="730px" spacing="19px">
                <Divider my="0px" />
                <Text
                    fontSize="10px"
                    fontWeight="300"
                    fontFamily="Roboto"
                    fontStyle="italic"
                    lineHeight="17px"
                    color={colors.lightGrayBlack}
                >
                    *{filteredData?.disclaimer}
                </Text>
            </Stack>
        </Flex>
    )
}

export default BlogArticle
